import _ from 'lodash';

import type { ErrorFieldType } from '@/types';

type ApiValidationResponse = {
  message?: string;
  errors?: Record<string, string[]>;
};

/**
 * Parse une erreur 422 de l'API en ErrorFieldType[].
 * Les champs imbriqués (adresse.identifiant, tresorerie.code) sont mappés au champ parent.
 */
export const parseApiValidationErrors = (response: ApiValidationResponse): ErrorFieldType[] => {
  const errors: ErrorFieldType[] = [];

  if (_.isNil(response?.errors)) return errors;

  _.forEach(response.errors, (messages, fieldPath) => {
    const id = _.head(_.split(fieldPath, '.'));
    const message = _.head(messages) || 'Champ invalide';

    if (id && !_.some(errors, { id })) {
      errors.push({ id, message });
    }
  });

  return errors;
};

const BUSINESS_ERROR_FIELD_MAPPING: { pattern: RegExp; fieldId: string }[] = [
  {
    pattern: /code interne .* est déjà utilisé/i,
    fieldId: 'codeInterne',
  },
];

/**
 * Parse une erreur métier 500 de l'API.
 * Retourne les erreurs de champs mappées et le message à afficher dans le toast.
 */
export const parseApiBusinessError = (errorResponse: {
  message?: string | { message?: string };
}): { fieldErrors: ErrorFieldType[]; toastMessage: string } => {
  const message =
    typeof errorResponse?.message === 'object'
      ? errorResponse.message.message
      : errorResponse?.message;

  const toastMessage = message || "L'enregistrement de la régie a échoué";
  const fieldErrors: ErrorFieldType[] = [];

  if (message) {
    _.forEach(BUSINESS_ERROR_FIELD_MAPPING, ({ pattern, fieldId }) => {
      if (pattern.test(message)) {
        fieldErrors.push({ id: fieldId, message });
      }
    });
  }

  return { fieldErrors, toastMessage };
};

/**
 * Extrait le statut HTTP d'une erreur de requête quel que soit son format :
 * - AxiosError (rejet de `request` de @mairistem/tools) → response.status / status
 *   (son `code` est une chaîne, ex. 'ERR_BAD_REQUEST', jamais le statut)
 * - ErrorApi de @mairistem/fiche                       → code numérique
 */
export const getHttpStatus = (error: unknown): number | undefined => {
  const e = error as { response?: { status?: number }; status?: number; code?: unknown };

  return e?.response?.status ?? e?.status ?? (typeof e?.code === 'number' ? e.code : undefined);
};

/**
 * Extrait le message d'erreur backend depuis un objet d'erreur quel que soit son format.
 * Couvre les formats observés dans la codebase :
 * - string brute
 * - { message: string }                      → @mairistem/tools en prod, Error standard
 * - { message: { message: string } }         → format imbriqué legacy
 * - { response: { data: { message: string } } } → axios standard
 * - { data: { message: string } }            → réponses partielles
 * - { body: { message: string } }            → fetch
 * Retourne le fallback si aucun chemin n'aboutit à une string.
 */
export const extractApiErrorMessage = (
  error: unknown,
  fallback = 'Une erreur est survenue',
): string => {
  if (typeof error === 'string') return error;

  const e = error as {
    message?: string | { message?: string };
    response?: { data?: { message?: string } };
    data?: { message?: string };
    body?: { message?: string };
  };

  if (typeof e?.message === 'string') return e.message;
  if (typeof e?.message === 'object' && typeof e.message?.message === 'string') {
    return e.message.message;
  }
  if (typeof e?.response?.data?.message === 'string') return e.response.data.message;
  if (typeof e?.data?.message === 'string') return e.data.message;
  if (typeof e?.body?.message === 'string') return e.body.message;

  return fallback;
};

/**
 * Message du toast d'échec d'un encaissement : le refus métier de l'API (422) quand il en porte
 * un — titre de paiement affecté à un produit non éligible, par exemple —, le message générique
 * sinon : une erreur technique n'a rien d'utile à dire au régisseur.
 */
export const getMessageErreurEncaissement = (error: unknown): string => {
  const messageGenerique = 'Encaissement impossible';

  return getHttpStatus(error) === 422
    ? extractApiErrorMessage(error, messageGenerique)
    : messageGenerique;
};
